/**
 * <AccountPickerField /> — a richer "pick a cash account" control to
 * replace the 5-6 duplicated <Select> blocks scattered across forms.
 *
 * What it gives you over a plain <Select>:
 *
 *   • **Per-option type avatar** (bank / cash / wallet icon) so the
 *     user can glance-recognise their accounts.
 *   • **Inline balance + currency** in each option ("Bancolombia
 *     ahorros · ARS · $1.2M") so the user picks the right one without
 *     opening the dashboard.
 *   • **Search by name** when the user has more than 6 accounts.
 *   • **Filter by currency** (optional) — e.g. for cross-currency
 *     transfers, hide accounts the operation can't target.
 *   • **Exclude IDs** — pass the source account id to keep it out of
 *     the destination dropdown automatically.
 *   • **Empty state** with a hint pointing to ``/accounts`` when the
 *     user has zero matching accounts.
 *
 * It composes with `<FormField>` so the parent gets the same error /
 * hint / a11y treatment as every other form input.
 *
 * Usage:
 *
 *   <AccountPickerField
 *     label="Desde" name="sourceId" required
 *     accounts={accounts}
 *     value={form.values.sourceId}
 *     onChange={(id) => form.setValue("sourceId", id)}
 *     onBlur={() => form.markTouched("sourceId")}
 *     error={form.touched.sourceId ? form.errors.sourceId : undefined}
 *     placeholder="Cuenta origen"
 *     // optional:
 *     excludeIds={[form.values.destId]}
 *     currencyFilter="ARS"
 *     isEs={true}
 *   />
 */
import { useMemo, useState } from "react";
import { Landmark, Wallet, CreditCard, Search } from "lucide-react";
import { FormField } from "./form-ux";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { formatMoney } from "../lib/api";


/** Icon + tint for each account type. Centralised so every consumer
 *  picks up future tweaks for free (e.g. adding a "crypto" type). */
const TYPE_VISUALS = {
  bank:   { Icon: Landmark,   tint: "text-sky-700 bg-sky-100" },
  cash:   { Icon: Wallet,     tint: "text-emerald-700 bg-emerald-100" },
  wallet: { Icon: CreditCard, tint: "text-violet-700 bg-violet-100" },
};


export function AccountPickerField({
  label,
  name,
  required = false,
  hint,
  error,
  className = "",

  accounts = [],
  value,                               // selected account id (string)
  onChange,                            // (id: string) => void
  onBlur,
  placeholder,
  excludeIds = [],                     // ids to hide (e.g. source from dest)
  currencyFilter,                      // e.g. "ARS" — hide others
  searchThreshold = 6,                 // show search when > N accounts
  isEs = true,
  emptyStateHref = "/accounts",
  testid,
  // iter-169zze · optional "primary account" sentinel row pinned at
  // the top of the dropdown. When the user picks it, ``onChange`` is
  // called with ``""`` (empty string) so callers keep the same "no
  // explicit account → use primary" convention they already used with
  // their hand-rolled <Select>. Pass null to hide the row.
  //
  //   primaryOption={{ label: "Cuenta principal (auto)" }}
  //
  // The sentinel value sent into the inner Radix <Select> is the
  // literal "_primary" string; we map it back/forth at the boundary.
  primaryOption = null,
}) {
  const [query, setQuery] = useState("");

  // ── Filter pipeline: exclude → currency → search ─────────────────
  const filtered = useMemo(() => {
    const ex = new Set(excludeIds.filter(Boolean));
    let rows = accounts.filter((a) => !ex.has(a.id));
    if (currencyFilter) {
      const c = String(currencyFilter).toUpperCase();
      rows = rows.filter((a) => String(a.currency).toUpperCase() === c);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) =>
        a.name?.toLowerCase().includes(q)
        || a.institution?.toLowerCase().includes(q)
        || a.currency?.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [accounts, excludeIds, currencyFilter, query]);

  const showSearch = accounts.length > searchThreshold;
  const isEmpty = filtered.length === 0;

  return (
    <FormField
      label={label}
      name={name}
      required={required}
      hint={hint}
      error={error}
      className={className}
      testid={testid}
    >
      <Select
        value={value || (primaryOption ? "_primary" : "")}
        onValueChange={(v) => { onChange?.(v === "_primary" ? "" : v); }}
      >
        <SelectTrigger
          data-testid={`account-picker-${name}`}
          aria-invalid={error ? "true" : undefined}
          onBlur={onBlur}
          className="h-12 rounded-xl"
        >
          <SelectValue
            placeholder={placeholder || (isEs ? "Elige una cuenta" : "Pick an account")}
          >
            {value && (() => {
              const a = accounts.find((x) => x.id === value);
              if (!a) return value;
              return <AccountChip a={a} compact />;
            })()}
            {!value && primaryOption && (
              <span className="text-sm text-slate-700">{primaryOption.label}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {showSearch && (
            <div
              className="px-2 py-1.5 sticky top-0 bg-white border-b border-slate-100 z-10"
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={isEs ? "Buscar cuenta…" : "Search…"}
                  className="pl-7 h-8 text-xs"
                  data-testid={`account-picker-${name}-search`}
                />
              </div>
            </div>
          )}
          {primaryOption && (
            <SelectItem
              value="_primary"
              data-testid={`account-picker-${name}-primary`}
              className="py-2"
            >
              <span className="text-sm font-medium text-slate-700">
                {primaryOption.label}
              </span>
            </SelectItem>
          )}
          {isEmpty && !primaryOption ? (
            <div
              className="px-3 py-4 text-xs text-slate-500 text-center"
              data-testid={`account-picker-${name}-empty`}
            >
              {currencyFilter
                ? (isEs
                    ? `No tienes cuentas en ${currencyFilter}.`
                    : `No accounts in ${currencyFilter}.`)
                : (isEs
                    ? "No tienes cuentas todavía."
                    : "You don't have accounts yet.")}
              <div className="mt-1">
                <a href={emptyStateHref} className="text-emerald-700 underline">
                  {isEs ? "Crear una" : "Create one"}
                </a>
              </div>
            </div>
          ) : (
            filtered.map((a) => (
              <SelectItem
                key={a.id}
                value={a.id}
                data-testid={`account-picker-${name}-${a.id}`}
                className="py-2"
              >
                <AccountChip a={a} />
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </FormField>
  );
}


/** Renders one account with avatar + name + meta. Used both inside
 *  the dropdown rows and (compact) in the selected-value chip. */
function AccountChip({ a, compact = false }) {
  const visual = TYPE_VISUALS[a.type] || TYPE_VISUALS.bank;
  const Icon = visual.Icon;
  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span
        className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 ${visual.tint}`}
        aria-hidden="true"
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="flex flex-col min-w-0 text-left">
        <span className="text-sm font-medium text-slate-900 truncate">
          {a.name}
        </span>
        {!compact && (
          <span className="text-[11px] text-slate-500 truncate">
            {a.institution ? `${a.institution} · ` : ""}
            {a.currency} · {formatMoney(a.balance, a.currency)}
          </span>
        )}
      </span>
    </span>
  );
}


export default AccountPickerField;
